import { debugLog } from "./debug";

export type CaptureStatus = "partial" | "complete" | "disqualified" | "test";

type Attribution = {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  utm_term: string;
  fbclid: string;
  /** ms timestamp when the fbclid was first seen, used to build _fbc. */
  fbclid_ts: number;
};

export type LeadContactInput = {
  name: string;
  email: string;
  phone: string;
  consent: boolean;
};

export type LeadPayload = {
  session_id: string;
  status: CaptureStatus;
  last_step: number;
  answers: Record<string, string>;
  contact: { first_name: string; last_name: string; email: string; phone: string };
  consent: boolean;
  utm: Omit<Attribution, "fbclid" | "fbclid_ts">;
  fbclid: string;
  fbp: string;
  fbc: string;
  page_url: string;
  timestamp: string;
};

const SESSION_KEY = "svip_session_id";
const ATTRIBUTION_KEY = "svip_attribution";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

let memorySessionId: string | null = null;
let memoryAttribution: Attribution | null = null;

export function uuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function getSessionId(): string {
  if (memorySessionId) return memorySessionId;
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      memorySessionId = stored;
      return stored;
    }
  } catch {
    /* storage unavailable */
  }
  memorySessionId = uuid();
  try {
    sessionStorage.setItem(SESSION_KEY, memorySessionId);
  } catch {
    /* storage unavailable */
  }
  return memorySessionId;
}

/** Reads UTM + fbclid from the URL on first load and persists them for the session. */
export function captureAttribution(): void {
  const stored = readStoredAttribution();
  const params = new URLSearchParams(window.location.search);
  const hasUrlData = UTM_KEYS.some((k) => params.get(k)) || Boolean(params.get("fbclid"));

  if (!hasUrlData && stored) {
    memoryAttribution = stored;
    return;
  }

  const fbclid = params.get("fbclid") ?? stored?.fbclid ?? "";
  const attribution: Attribution = {
    utm_source: params.get("utm_source") ?? stored?.utm_source ?? "",
    utm_medium: params.get("utm_medium") ?? stored?.utm_medium ?? "",
    utm_campaign: params.get("utm_campaign") ?? stored?.utm_campaign ?? "",
    utm_content: params.get("utm_content") ?? stored?.utm_content ?? "",
    utm_term: params.get("utm_term") ?? stored?.utm_term ?? "",
    fbclid,
    fbclid_ts:
      params.get("fbclid") && params.get("fbclid") !== stored?.fbclid
        ? Date.now()
        : stored?.fbclid_ts ?? (fbclid ? Date.now() : 0),
  };
  memoryAttribution = attribution;
  try {
    sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch {
    /* storage unavailable */
  }
}

export function buildLeadPayload(input: {
  sessionId: string;
  status: CaptureStatus;
  lastStep: number;
  answers: Record<string, string>;
  contact: LeadContactInput;
}): LeadPayload {
  const attribution = memoryAttribution ?? readStoredAttribution() ?? emptyAttribution();
  const [firstName = "", ...rest] = input.contact.name.trim().split(/\s+/).filter(Boolean);
  const fbcCookie = getCookie("_fbc");
  const fbc =
    fbcCookie ||
    (attribution.fbclid ? `fb.1.${attribution.fbclid_ts || Date.now()}.${attribution.fbclid}` : "");

  return {
    session_id: input.sessionId,
    status: input.status,
    last_step: input.lastStep,
    answers: input.answers,
    contact: {
      first_name: firstName,
      last_name: rest.join(" "),
      email: input.contact.email.trim(),
      phone: input.contact.phone.trim(),
    },
    consent: input.contact.consent,
    utm: {
      utm_source: attribution.utm_source,
      utm_medium: attribution.utm_medium,
      utm_campaign: attribution.utm_campaign,
      utm_content: attribution.utm_content,
      utm_term: attribution.utm_term,
    },
    fbclid: attribution.fbclid,
    fbp: getCookie("_fbp"),
    fbc,
    page_url: window.location.href,
    timestamp: new Date().toISOString(),
  };
}

/** Fire-and-forget POST. Never throws, never blocks the user. */
export function sendLeadCapture(webhookUrl: string, payload: LeadPayload): void {
  if (!webhookUrl) {
    debugLog("[lead-capture] WEBHOOK_URL vacío. Payload:", payload);
    return;
  }
  debugLog("[lead-capture] POST", payload.status, payload);
  const body = JSON.stringify(payload);
  // If the browser blocks the JSON request (CORS), retry as a simple request
  // so the webhook still receives the data. The CRM upserts by email/phone.
  const fallback = (err: unknown) => {
    debugLog("[lead-capture] Envío JSON bloqueado, reintentando sin CORS:", err);
    try {
      fetch(webhookUrl, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "text/plain;charset=UTF-8" },
        body,
        keepalive: true,
      }).catch((e) => debugLog("[lead-capture] Error enviando:", e));
    } catch (e) {
      debugLog("[lead-capture] Error enviando:", e);
    }
  };
  try {
    fetch(webhookUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    })
      .then((res) => debugLog("[lead-capture] Respuesta del webhook:", res.status))
      .catch(fallback);
  } catch (err) {
    fallback(err);
  }
}

/** Sends a sample payload with status "test". Available in the console with ?debug=1. */
export function sendTestLead(webhookUrl: string): void {
  sendLeadCapture(
    webhookUrl,
    buildLeadPayload({
      sessionId: `test-${uuid()}`,
      status: "test",
      lastStep: 7,
      answers: {
        industry: "Clínica dental",
        source: "Facebook",
        responder: "Recepción",
        problem: "Tardamos en responder",
        revenue: "$30K – $100K",
        role: "Dueño",
      },
      contact: {
        name: "Prueba Webhook",
        email: "prueba@studiowebvip.com",
        phone: "3050000000",
        consent: true,
      },
    })
  );
}

function readStoredAttribution(): Attribution | null {
  try {
    const raw = sessionStorage.getItem(ATTRIBUTION_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}

function emptyAttribution(): Attribution {
  return {
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_content: "",
    utm_term: "",
    fbclid: "",
    fbclid_ts: 0,
  };
}

function getCookie(name: string): string {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
}
